// Iter81 — Partner Storefront menu redirect.
//
// The sidebar "Partner Storefront" link points here. We resolve the
// logged-in partner's slug from /api/partner/me and immediately redirect
// to /shop/partner/:slug so the partner sees their live public page.
//
// If no slug exists yet (incomplete profile), render a setup prompt
// instead of a broken page.
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, Store, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function PartnerStorefrontRedirect() {
  const [state, setState] = useState({ loading: true, slug: null, approved: false, err: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/partner/me");
        if (!alive) return;
        const slug = data?.profile?.slug;
        const approved = data?.profile?.compliance?.status === "approved";
        setState({ loading: false, slug, approved, err: "" });
      } catch (e) {
        if (alive) setState({ loading: false, slug: null, approved: false, err: apiErrorMessage(e) });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="partner-storefront-redirect-loading">
        <div className="text-lifted-dim text-sm flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Loading your storefront…
        </div>
      </div>
    );
  }

  // Approved partners with a slug → redirect to live storefront
  if (state.slug && state.approved) {
    return <Navigate to={`/shop/partner/${state.slug}`} replace />;
  }

  // Otherwise show a setup prompt
  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="partner-storefront-setup-prompt">
      <div className="glass-panel p-6 border border-amber-400/30 bg-amber-400/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-300 shrink-0 mt-1" size={20} />
          <div className="space-y-3 flex-1">
            <div>
              <h2 className="font-anton text-2xl uppercase text-white">Storefront not live yet</h2>
              <p className="text-sm text-lifted-dim mt-1">
                {state.err
                  ? state.err
                  : !state.slug
                  ? "Complete your partner profile to generate your public storefront."
                  : "Your storefront is pending admin approval. Once approved it'll be visible to customers."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Link
                to="/partner"
                className="btn-lifted text-xs inline-flex items-center gap-1"
                data-testid="partner-storefront-complete-setup"
              >
                <Store size={12} /> Complete Storefront Setup
              </Link>
              {state.slug && (
                <Link
                  to={`/shop/partner/${state.slug}`}
                  className="btn-ghost text-xs"
                  data-testid="partner-storefront-preview-link"
                >
                  Preview public page
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
